import { EMPTY, forkJoin, Observable, of } from 'rxjs'
import { concatMap, switchMap, tap } from 'rxjs/operators'

import { Injectable } from '@angular/core'
import { Feature } from '@classes/Feature'
import { FeatureProperty } from '@classes/FeatureProperty'
import { Model } from '@classes/Model'
import { Project } from '@classes/Project'
import { Scene } from '@classes/Scene'
import { SceneProperty } from '@classes/SceneProperty'

import { CreateFeatureOptions, CreateGroupOptions, FeatureService } from './feature.service'
import { ModelService } from './model.service'
import { ProjectService } from './project.service'
import { SceneService } from './scene.service'
import { UndoService } from './undo.service'

export interface Command {
    executed: boolean
    type: string
    execute(): Observable<any>
    undo(): Observable<any>
    id?: number
}

type CommandTypes = 'Feature' | 'FeatureAndProperty' | 'FeatureProperties' | 'FeatureProperty' | 'Model' | 'Project' | 'SceneProperty' | 'Scene' | 'SceneProperties'

export class UpdateCommand<Type> implements Command {
    public executed = false

    constructor(
        public type: CommandTypes,
        private update: (id?: number) => Observable<Type>,
        private revert: (id?: number) => Observable<Type>,
        public id?: number,
    ) { }

    execute(): Observable<Type> {
        if (this.executed) return EMPTY
        return this.update(this.id).pipe(tap(() => this.executed = true))
    }

    undo(): Observable<Type> {
        if (!this.executed) return EMPTY
        return this.revert(this.id).pipe(tap(() => this.executed = false))
    }
}

export class CreateCommand<Type> implements Command {
    private _id: number
    public executed = false
    public previousID: number

    get id() { return this._id }
    set id(id: number) {
        this.previousID = this.id
        this._id = id
    }

    constructor(
        public type: CommandTypes,
        private create: () => Observable<Type>,
        private destroy: () => Observable<Type>,
    ) { }

    execute(): Observable<Type> {
        if (this.executed) return EMPTY

        return this.create().pipe(
            tap(creation => {
                this.id = creation['id']

                this.executed = true
            })
        )
    }

    undo(): Observable<Type> {
        if (!this.executed) return EMPTY

        return this.destroy().pipe(
            tap(() => this.executed = false)
        )
    }
}

class DeleteCommand<Type> implements Command {
    public executed = false
    public type = 'Delete'
    public id: number

    /**
     * @returns undefined. TODO: figure out how to do undo of delete.
     */
    constructor(
        private newThing: Type,
        private create: (newThing: Type) => Observable<Type>,
        private destroy: (newThing: Type) => Observable<Type>) {
        if (!newThing || !create || !destroy) return undefined
    }

    execute(): Observable<Type> {
        if (!this.executed) return this.destroy(this.newThing).pipe(tap(() => this.executed = true))
    }

    undo(): Observable<Type> {
        if (this.executed) return this.create(this.newThing).pipe(tap(result => {
            this.newThing = result
            this.executed = false
        }))
    }
}

@Injectable({
    providedIn: 'root'
})
export class CommandService {
    public readonly update = {
        project: (project: Project) => {
            const previous = this.projectService.currentProject
            const update = () => this.projectService.updateProject(project)
            const revert = () => this.projectService.updateProject(previous)
            const command = new UpdateCommand<Project>('Project', update, revert)

            return this._execute<Feature>(command)
        },
        sceneProperty: (property: SceneProperty, oldProperty: SceneProperty) => {
            const scene = this.sceneService.scenes.find(scene => scene.id == property.sceneID)
            const update = (id: number) => {
                property.id = id
                return this.sceneService.updateSceneProperty(property)
            }
            const revert = () => {
                return this.sceneService.updateSceneProperty(oldProperty)
            }
            const command = new UpdateCommand<SceneProperty>('SceneProperty', update, revert, property.id)

            return this._execute<SceneProperty>(command)
        },
        sceneProperties: (properties: SceneProperty[], oldProperties: SceneProperty[]) => {
            if (!properties || properties.length === 0) return EMPTY
            const sceneID = properties[0].sceneID
            const updateCommands: Observable<SceneProperty>[] = []
            const revertCommands: Observable<SceneProperty>[] = []

            properties.forEach(property => {
                updateCommands.push(this.sceneService.updateSceneProperty(property))
            })

            oldProperties.forEach(property => {
                revertCommands.unshift(this.sceneService.updateSceneProperty(property))
            })

            const update = () => forkJoin(updateCommands)
            const revert = () => forkJoin(revertCommands)

            const command = new UpdateCommand<SceneProperty[]>('SceneProperties', update, revert, sceneID)
            return this._execute<SceneProperty[]>(command)
        },
        scene: (scene: Scene, attribute: string, newValue: any, oldValue: any) => {
            const update = () => {
                scene[attribute] = newValue
                return this.sceneService.updateScene(scene, true)
            }
            const revert = () => {
                scene[attribute] = oldValue
                return this.sceneService.updateScene(scene, true)
            }

            const command = new UpdateCommand<Scene>('Scene', update, revert, scene.id)

            return this._execute<Scene>(command)
        },
        feature: (feature: Feature) => {
            const previous = this.featureService.getFeature(feature.id)
            const update = (id: number) => {
                feature.id = id
                return this.featureService.updateFeature(feature)
            }
            const revert = (id: number) => {
                previous.id = id
                return this.featureService.updateFeature(previous)
            }
            const command = new UpdateCommand<Feature>('Feature', update, revert, feature.id)

            return this._execute<Feature>(command)
        },
        featureProperty: (property: FeatureProperty) => {
            const feature = this.featureService.getFeature(property.featureID)
            const previous = feature.properties.find(p => p.key == property.key)

            const update = () => this.featureService.updateFeatureProperty(property)
            const revert = () => this.featureService.updateFeatureProperty(previous)

            const command = new UpdateCommand<FeatureProperty>('FeatureProperty', update, revert, feature.id)

            return this._execute<Feature>(command)
        },
        featureProperties: (properties: FeatureProperty[]) => {
            const featureID = properties[0].featureID
            const updateCommands: Observable<FeatureProperty>[] = []
            const revertCommands: Observable<FeatureProperty>[] = []

            properties.forEach(property => {
                const feature = this.featureService.getFeature(featureID)
                const previous = feature.properties.find(p => p.key == property.key)

                updateCommands.push(this.featureService.updateFeatureProperty(property))
                revertCommands.unshift(this.featureService.updateFeatureProperty(previous))
            })

            const update = () => forkJoin(updateCommands)
            const revert = () => forkJoin(revertCommands)

            const command = new UpdateCommand<FeatureProperty[]>('FeatureProperties', update, revert, featureID)

            return this._execute<FeatureProperty[]>(command)
        },
        featureAndProperty: (feature: Feature, property: FeatureProperty) => {
            const previousFeature = this.featureService.getFeature(feature.id)
            const previousProperty = previousFeature.properties.find(p => p.key == property.key)

            const updateFeature = () => this.featureService.updateFeature(feature)
            const updateProperty = () => this.featureService.updateFeatureProperty(property)

            const revertFeature = () => this.featureService.updateFeature(previousFeature)
            const revertProperty = () => this.featureService.updateFeatureProperty(previousProperty)

            const update = () => forkJoin([updateFeature(), updateProperty()])
            const revert = () => forkJoin([revertFeature(), revertProperty()])

            const command = new UpdateCommand<any>('FeatureAndProperty', update, revert, feature.id)

            return this._execute<Feature>(command)
        },
        model: (model: Model) => {
            const previous = this.modelService.previousModel ?? this.modelService.models.find(a => a.id == model.id)
            const update = (id: number) => {
                model.id = id
                return this.modelService.updateModel(model, { updateLocally: true, toast: true })
            }
            const revert = (id: number) => {
                previous.id = id
                return this.modelService.updateModel(previous, { updateLocally: true })
            }
            const command = new UpdateCommand<Model>('Model', update, revert, model.id)

            return this._execute<Model>(command)
        },
    }

    public readonly create = {
        feature: (feature: Feature, options?: Partial<CreateFeatureOptions>) => {
            const create = () => this.featureService.createFeature(feature, options).pipe(tap(f => feature = f))
            const destroy = () => this.featureService.deleteFeature(feature)
            const command = new CreateCommand<Feature>('Feature', create, destroy)

            return this._execute<Feature>(command)
        },
        group: (options: CreateGroupOptions = {}) => {
            let group
            const create = () => this.featureService.createFeatureGroup(options)
                .pipe(tap(f => group = f))
            const destroy = () =>
                forkJoin(options.children?.slice().map(f => this.featureService.updateFeature(f)))
                    .pipe(switchMap(() => this.featureService.deleteFeature(group)))

            const command = new CreateCommand<Feature>('Feature', create, destroy)

            return this._execute<Feature>(command)
        },
        model: (model: Model, ...files: File[]) => {
            const create = () => this.modelService.createModelWithS3(files, { name: model.name, description: model.description }).pipe(tap(f => model = f))
            const destroy = () => this.modelService.deleteModel(model)
            const command = new CreateCommand<Model>('Model', create, destroy)

            return this._execute<Model>(command)
        },
    }

    public readonly delete = {
        // action: () => { },
        // model: () => { },
        // feature: () => { },
        // featureProperty: () => { },
        // interaction: () => { },
        // permission: () => { },
        // project: () => { },
        // scene: () => { },
        // sceneProperty: () => { },
        // user: () => { },
    }

    constructor(
        private modelService: ModelService,
        private featureService: FeatureService,
        private projectService: ProjectService,
        private sceneService: SceneService,
        private undoService: UndoService,
    ) { }

    /**
     * Calls execute on a command and stacks for undoRedo
     * @param command
     * @returns The Observable result of the command's execute function.
     */
    private _execute<Type>(command: Command): Observable<Type> {
        return command.execute().pipe(
            concatMap((value, index) => index === 0
                ? of(value).pipe(
                    tap(() => this.undoService.stack(command))
                )
                : of(value)
            ))
    }
}